<template>
	<div
		class="cog-checkable cog-input"
		:class="[{'cog-checkable--allow-other': allowOther}, hasColumns ? columnClasses : 'cog-checkable--inline', readonly ? 'is-read-only' : '']"
	>
		<div v-if="readonly" :class="{'cog-checkable--read-only-images': hasImageOption}">
			<template v-if="!selectedOptions.length">&nbsp;</template>
			<template v-else>
				<template v-if="columns > 0">
					<template v-for="(item, i) in selectedOptions">
						<div
							:key="item.value"
							:class="hasColumns ? 'cog-inner-col__item' : null"
							class="cog-checkable__item"
							:style="`order: ${optionOrder[i]}`"
						>
							<!-- whitespace on the following line is significant -->
							<slot name="readonly-item" :item="item"><span :class="{'cog-image-item': item.image || item.svg || item.choiceImage}"><c-generated-avatar v-if="item.svg" :svg="item.svg" /><img v-else-if="item.image" :src="item.image" height="50" width="50" class="cog-lookup-image cog-avatar" /><div v-else-if="item.choiceImage" :style="{ backgroundImage: `url(${item.choiceImage})`}" class="cog-choice-image" />{{ item.displayValue | breakWords }}</span></slot>
						</div>
					</template>
				</template>
				<template v-else>
					<template v-for="(item, index) in selectedOptions">{{ index && !hasImageOption ? ', ' : '' }}<slot name="readonly-item" :item="item"><span :key="item.value" :class="{'cog-image-item': item.image || item.svg || item.choiceImage }"><c-generated-avatar v-if="item.svg" :svg="item.svg" /><img v-else-if="item.image" :src="item.image" height="50" width="50" class="cog-lookup-image cog-avatar" /><div v-else-if="item.choiceImage" :style="{ backgroundImage: `url(${item.choiceImage})`}" class="cog-choice-image" />{{ item.displayValue | breakWords }}</span></slot></template>
				</template>
			</template>
		</div>
		<template v-else>
			<el-checkbox-group
				v-model="internalValue"
				:role="null"
				:label="null"
				@change="handleChange"
			>
				<el-checkbox
					v-for="(item, i) in options"
					:key="item.value"
					:label="item.value"
					class="cog-checkable__item"
					:class="{'cog-inner-col__item': hasColumns}"
					:style="`order: ${optionOrder[i]}`"
				>
					<div :class="{'cog-image-item': item.image || item.svg || item.choiceImage}">
						<c-generated-avatar v-if="item.svg" :svg="item.svg" />
						<img v-else-if="item.image" :src="item.image" height="50" width="50" class="cog-lookup-image cog-avatar" />
						<div v-else-if="item.choiceImage" :style="{ backgroundImage: `url(${item.choiceImage})`}" class="cog-choice-image" />
						{{ item.displayValue | breakWords }}
					</div>
				</el-checkbox>
				<el-checkbox
					v-if="allowOther"
					:value="otherIsChecked"
					class="cog-checkable__allow-other"
					:class="{ 'is-checked': otherIsChecked, 'cog-inner-col__item': hasColumns }"
					:style="`order: ${optionOrder[options.length]}`"
					:label="otherValue"
					@change="handleOtherCheckChange"
				>
					<el-input ref="other" v-model="otherValue" :placeholder="$resource('field-choice-other-fill-in')" @focus="handleOtherInputFocus" @blur="handleOtherInputBlur" />
				</el-checkbox>
			</el-checkbox-group>
		</template>
	</div>
</template>

<script>
	import '../style/_fancy-checkables.scss';

	import Vue from 'vue';
	import { CheckboxGroup, Checkbox, Input } from '@cognitoforms/element-ui';
	import columnizedOptions from 'src/mixins/columnized-options';
	import resetComponentData from 'src/mixins/resetComponentData';
	import CGeneratedAvatar from './GeneratedAvatar.vue';

	Vue.use(CheckboxGroup);
	Vue.use(Checkbox);
	Vue.use(Input);

	export default {
		name: 'CCheckboxGroup',
		components: { CGeneratedAvatar },
		mixins: [columnizedOptions, resetComponentData],
		inject: ['$resource'],
		inheritAttrs: false,
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			value: {
				type: Array,
				default: () => []
			},
			readonly: Boolean,
			allowOther: Boolean
		},
		data() {
			const otherValue = this.findOtherValue(this.value);
			return {
				internalValue: this.value,
				otherValue,
				otherIsChecked: this.allowOther && !!otherValue
			};
		},
		computed: {
			selectedOptions() {
				const selected = this.options.filter(o => this.internalValue.includes(o.value));
				if (this.otherIsChecked && this.otherValue)
					selected.push({ displayValue: this.otherValue, value: this.otherValue });
				return selected;
			},
			hasImageOption() {
				return this.options?.some(option => option.image || option.svg || option.choiceImage);
			}
		},
		watch: {
			value(valueProp) {
				this.internalValue = valueProp;
				const newOtherValue = this.findOtherValue(valueProp);
				if (this.allowOther && newOtherValue) {
					this.otherIsChecked = true;
					this.otherValue = newOtherValue;
				}
			}
		},
		methods: {
			findOtherValue(selectedOptions) {
				return selectedOptions.find(selected => !this.options.some(o => o.value === selected));
			},
			handleOtherCheckChange(isChecked) {
				this.otherIsChecked = isChecked;
				if (isChecked && !this.readonly && this.$refs.other)
					this.$refs.other.focus();
				this.handleChange(this.internalValue, null);
			},
			handleChange(value, ev) {
				this.internalValue = value;
				this.$emit('change', this.selectedOptions.map(o => o.value), ev);
			},

			handleOtherInputFocus() {
				if (!this.otherIsChecked) {
					this.updating = true;
					this.handleOtherCheckChange(true);
				}
			},

			handleOtherInputBlur(ev) {
				if (!this.otherValue) {
					this.otherIsChecked = false;
					this.handleChange(this.selectedOptions.map(o => o.value), ev);
				}

				// Raise change if an "other" value has been added or modified
				if (this.otherValue && this.value.indexOf(this.otherValue) < 0) {
					this.handleChange(this.internalValue, ev);
				}
			}

		}
	};
</script>